import { Component, ElementRef, effect, input, output, signal, viewChild } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [LucideAngularModule],
  template: `
    <label class="date-shell">
      @if (label()) {
        <span>{{ label() }}</span>
      }
      <div class="date-control">
        <input
          type="text"
          class="date-text-input"
          inputmode="numeric"
          [required]="required()"
          [attr.aria-label]="ariaLabel() || label() || null"
          [value]="displayValue()"
          placeholder="dd/mm/aaaa"
          (input)="onTextInput($event)"
          (blur)="onTextBlur()"
          (keydown.enter)="onTextEnter($event)"
        />
        <button
          type="button"
          class="date-picker-btn"
          (click)="openNativePicker($event)"
          [attr.aria-label]="label() || 'Selecionar data'"
        >
          <lucide-icon name="calendar" [size]="16" [strokeWidth]="1.75"></lucide-icon>
        </button>
        <input
          #nativeDateInput
          type="date"
          class="date-native-input"
          [attr.aria-label]="ariaLabel() || label() || null"
          [required]="required()"
          [value]="isoValue()"
          (change)="onNativeDateChange($event)"
        />
      </div>
    </label>
  `,
  styles: [
    `
      .date-shell {
        display: grid;
        gap: 4px;
        width: 100%;
        min-width: 0;
      }

      .date-shell span {
        font-size: 12px;
        color: var(--faint);
      }

      .date-control {
        position: relative;
      }

      .date-text-input {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        height: 36px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        padding: 0 10px;
        font-size: 13px;
        padding-right: 38px;
      }

      .date-picker-btn {
        position: absolute;
        top: 50%;
        right: 4px;
        transform: translateY(-50%);
        width: 28px;
        height: 28px;
        border: none;
        border-radius: 8px;
        background: transparent;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .date-picker-btn:hover {
        background: color-mix(in srgb, var(--hover) 65%, transparent);
      }

      .date-picker-btn lucide-icon {
        color: var(--muted);
        display: inline-flex;
      }

      .date-native-input {
        position: absolute;
        top: 0;
        right: 0;
        width: 36px;
        height: 36px;
        opacity: 0;
        cursor: pointer;
        pointer-events: none;
      }
    `,
  ],
})
export class DatePickerComponent {
  readonly label = input<string>('');
  readonly ariaLabel = input<string>('');
  readonly value = input<string | null>(null);
  readonly valueChange = output<string | null>();
  readonly required = input<boolean>(false);
  private readonly nativeDateInput = viewChild<ElementRef<HTMLInputElement>>('nativeDateInput');

  protected readonly displayValue = signal('');
  protected readonly isoValue = signal<string>('');

  constructor() {
    effect(() => {
      const iso = this.normalizeIsoDate(this.value());
      this.isoValue.set(iso ?? '');
      this.displayValue.set(iso ? this.formatIsoToPtBr(iso) : '');
    });
  }

  protected onTextInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.displayValue.set(value);
  }

  protected onTextBlur(): void {
    this.commitTextValue();
  }

  protected onTextEnter(event: Event): void {
    event.preventDefault();
    this.commitTextValue();
  }

  protected onNativeDateChange(event: Event): void {
    const raw = (event.target as HTMLInputElement | null)?.value?.trim() ?? '';
    const iso = this.normalizeIsoDate(raw);
    if (!iso) {
      this.displayValue.set('');
      this.isoValue.set('');
      this.valueChange.emit(null);
      return;
    }
    this.displayValue.set(this.formatIsoToPtBr(iso));
    this.isoValue.set(iso);
    this.valueChange.emit(iso);
  }

  protected openNativePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const input = this.nativeDateInput()?.nativeElement;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  }

  private commitTextValue(): void {
    const raw = this.displayValue().trim();
    if (!raw.length) {
      this.isoValue.set('');
      this.valueChange.emit(null);
      return;
    }
    const parsed = this.parseDateTextToIso(raw);
    if (!parsed) {
      const fallback = this.normalizeIsoDate(this.value());
      this.displayValue.set(fallback ? this.formatIsoToPtBr(fallback) : '');
      this.isoValue.set(fallback ?? '');
      this.valueChange.emit(fallback);
      return;
    }
    this.displayValue.set(this.formatIsoToPtBr(parsed));
    this.isoValue.set(parsed);
    this.valueChange.emit(parsed);
  }

  private parseDateTextToIso(raw: string): string | null {
    const t = raw.trim();
    const iso = this.normalizeIsoDate(t);
    if (iso) return iso;
    const ptBr = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
    if (!ptBr) return null;
    const day = ptBr[1].padStart(2, '0');
    const month = ptBr[2].padStart(2, '0');
    const year = ptBr[3];
    return this.normalizeIsoDate(`${year}-${month}-${day}`);
  }

  private normalizeIsoDate(raw: string | null | undefined): string | null {
    const t = (raw ?? '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
    const [y, m, d] = t.split('-').map((v) => Number(v));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    const dt = new Date(Date.UTC(y, m - 1, d));
    const isValid =
      dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
    return isValid ? t : null;
  }

  private formatIsoToPtBr(iso: string): string {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }
}
